// src/components/withTranslationLock/withTranslationLock.js

import React from 'react';
import { TranslationContext } from '../googleTranslate/translationContext';
import PropTypes from 'prop-types';

function getDisplayName(WrappedComponent)
{ return WrappedComponent.displayName || WrappedComponent.name || 'Component'; }

function getDeepKeys(propType, prefix = '')
{   if (!propType) return [];

    if (propType === PropTypes.array || (propType.type && propType.type.name === 'array'))
        return [`${prefix}`];

    if (propType._chainedCheckType === 'arrayOf')
    {   const itemType = propType._typeChecker;
        return [`${prefix}[*]${getDeepKeys(itemType, '')}`];
    }

    if (propType._chainedCheckType === 'shape' && propType._shapeTypes)
    {   return Object.entries(propType._shapeTypes)
            .flatMap(([key, value]) =>
            {   const newPrefix =  prefix          ?
                                `${prefix}.${key}` :
                                             key   ;
                return getDeepKeys(value, newPrefix);
            });
    }

    if (propType === PropTypes.object || (propType.type && propType.type.name === 'object'))
        return [`${prefix}`];

    if (prefix)
        return [prefix];

    return [];
}

function compareValues(prev, next, path, visitedPrev = new WeakSet(), visitedNext = new WeakSet())
{   if (prev === next) return false;

// Handle null vs non-null
    if ((!prev && next) || (prev && !next)) return true;

// Handle arrays
    if (Array.isArray(prev))
    {   if (!Array.isArray(next) || prev.length !== next.length) return true;

        for (let i = 0; i < prev.length; i++)
            if (compareValues(prev[i], next[i], `${path}[${i}]`, visitedPrev, visitedNext))
                return true;
        return false;
    }

    // Handle objects
    if (typeof prev === 'object' && prev !== null)
    {   if (typeof next !== 'object' || next === null)
            return true;

        // Check for circular references
        if (visitedPrev.has(prev) || visitedNext.has(next))
            return false;

        visitedPrev.add(prev);
        visitedNext.add(next);

        const keys = new Set([...Object.keys(prev), ...Object.keys(next)]);

        for (let key of keys)
            if (compareValues(prev[key], next[key], `${path}.${key}`, visitedPrev, visitedNext))
                return true;
        
        return false;
    }

    // Fallback for primitive types
    return prev !== next;
}

function getValueByPath(obj, path)
{   if (!path) return obj;

    const parts = path.split('.');
    let current = obj;

    for (const part of parts)
    {   if (part.includes('[*]'))
        {   const arrayPath = part.split('[*]')[0];
            if (!current[arrayPath] || !Array.isArray(current[arrayPath]))
                return undefined;
            current = current[arrayPath];
        }
        else
        {   if (current === undefined || current === null)
                return undefined;
            current = current[part];
        }
    }

    return current;
}

function havePropsChanged(prevProps, nextProps, propTypes)
{   if (!propTypes)
    {   const prevKeys = Object.keys(prevProps);
        const nextKeys = Object.keys(nextProps);
        if (prevKeys.length !== nextKeys.length)
            return true;
        return prevKeys.some(key => prevProps[key] !== nextProps[key]);
}

    const paths = Object.entries(propTypes).flatMap(([key, type]) =>
        { return getDeepKeys(type, key); });

    // Initialize WeakSets for tracking visited objects
    const visitedPrev = new WeakSet();
    const visitedNext = new WeakSet();

    return paths.some(path =>
    {   const prevValue = getValueByPath(prevProps, path);
        const nextValue = getValueByPath(nextProps, path);
        return compareValues(prevValue, nextValue, path, visitedPrev, visitedNext);
    });
}

const withTranslationLock = (WrappedComponent) => {
    class WithTranslationLock extends React.Component {
        static displayName = `withTranslationLock(${getDisplayName(WrappedComponent)})`;
        static contextType = TranslationContext; // Ensure correct import
        
        constructor(props)
        {   super(props);
        this.state =
        {   isTranslating: false,
            translationVersion: 0,
            lastTranslatedProps: props
        };
        this.contentRef         = React.createRef();
        this.translationTimeout = null;
        this.observer           = null;
        }

        componentDidMount()
        {   if (this.shouldTriggerTranslation())
                this.handleTranslation();
            this.setupTranslationObserver();
        }

        componentDidUpdate(prevProps)
        {   if ( havePropsChanged(prevProps, this.props, WrappedComponent.propTypes) && 
                 this.shouldTriggerTranslation()
               )
                this.handleTranslation();
        }

        componentWillUnmount()
        {   if (this.translationTimeout)
                clearTimeout(this.translationTimeout);
            if (this.observer)
                this.observer.disconnect();
        }

        setupTranslationObserver = () =>
        {   if (typeof window === 'undefined' || !this.contentRef.current)
                return;

        const config =
        {   attributes:    true, 
            childList:     true, 
            subtree:       true,
            characterData: true 
        };

        this.observer = new MutationObserver((mutations) =>
        {   const isGoogleTranslateChange = mutations.some(mutation =>
            {   return mutation.target.classList?.contains('goog-te-combo')   ||
                       mutation.target.classList?.contains('goog-te-banner');
            });

            if (isGoogleTranslateChange && !this.state.isTranslating)
                this.handleTranslation();
        });

        this.observer.observe(document.body, config);
        };

        shouldTriggerTranslation = () => {
        const { translationActive, currentLanguage } = this.context;
        return (
            typeof window !== 'undefined' && 
            translationActive &&
            currentLanguage !== 'en' &&
            !this.state.isTranslating
        );
        };

        handleTranslation = () => {
        if (this.translationTimeout)
            clearTimeout(this.translationTimeout);

        this.setState({ 
            isTranslating: true,
            lastTranslatedProps: this.props 
        }, () => {
            this.translationTimeout = setTimeout(() => {
            this.applyTranslation().finally(() => {
                this.setState(prevState => ({
                isTranslating: false,
                translationVersion: prevState.translationVersion + 1
                }));
            });
            }, 100);
        });
        };

        applyTranslation = () => {
        return new Promise((resolve) =>
        {   const { translationActive, currentLanguage } = this.context;

            if (!translationActive)
            {   resolve();
                return;
            }

            const language    = currentLanguage || 'en';
            const selectField = document.querySelector('.goog-te-combo');

            if (selectField && selectField.value !== language)
            {   selectField.value = language;
                const event       = new Event('change', { bubbles: true });
                selectField.dispatchEvent(event);
            
                    // Wait for translation to complete
                setTimeout(resolve, 500);
            }
            else resolve();
        });
        };

        shouldComponentUpdate(nextProps, nextState) {
        // Always allow updates when translation state changes
        if (
            this.state.isTranslating !== nextState.isTranslating ||
            this.state.translationVersion !== nextState.translationVersion
        ) {
            return true;
        }

        // Block updates during translation
        if (this.state.isTranslating) {
            return false;
        }

        // Use PropTypes-based comparison for prop changes
        return havePropsChanged(this.props, nextProps, WrappedComponent.propTypes);
        }

        render() {
        const { className, ...restProps } = this.props;
        const key                         = `translation-${this.state.translationVersion}`;
        
        const content = (
            <WrappedComponent 
            {...restProps}
            />
        );

        if (className) {
            // Assign the key to the wrapping div, not the WrappedComponent
            return (
            <div ref={this.contentRef} className={className} key={key}>
                {content}
            </div>
            );
        }

        return (
            <div ref={this.contentRef} style={{ display: 'contents' }} key={key}>
            {content}
            </div>
        );
        }
    }

WithTranslationLock.propTypes = {
    className: PropTypes.string,
};

return WithTranslationLock;
};

export default withTranslationLock;
